import { fetchPreregister, parseRequestError, recoverAccount, selectRecovery } from 'containers/Login/loginSlice'
import { passwordErrorMessage } from 'utils/validation'
import { useDispatch, useSelector } from 'react-redux'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import { accountRecoveryCode } from 'rest/urls'
import BackBtn from 'components/buttons/BackBtn'
import { useHistory } from 'react-router-dom'
import Input from 'components/inputs/Input'
import { useEffect, useState } from 'react'
import { STATUS } from 'utils/constants'
import { selectConfig } from 'appSlice'
import { POST } from 'rest/request'
import '../style.scss'
import './style.scss'

const TOKEN_TTL = 14 * 60 * 1000

const CHANNELS = {
  sms: 'sms',
  email: 'email',
}

const CODE_FIELDS = {
  [CHANNELS.sms]: 'smsCode',
  [CHANNELS.email]: 'emailCode',
}

const AccountRecovery = () => {
  const recovery = useSelector(selectRecovery)
  const config = useSelector(selectConfig)
  const [status, setStatus] = useState(STATUS.idle)
  const [sendingChannel, setSendingChannel] = useState('')
  const [sent, setSent] = useState({})
  const [form, setForm] = useState({ password: '', passwordSec: '', smsCode: '', emailCode: '' })
  const [warning, setWarning] = useState({})
  const [error, setError] = useState('')

  const dispatch = useDispatch()
  const { push, replace } = useHistory()
  const { t } = useTranslation('form')

  const isLoading = status === STATUS.pending
  const isFormStep = Object.values(sent).some(Boolean)
  const requiresBoth = recovery?.requiredCodes === 'both'
  const channels = (recovery?.channels || []).filter(c => c === CHANNELS.sms || c === CHANNELS.email)
  const refs = []

  useEffect(() => {
    if (!recovery && status !== STATUS.succeed) replace('/login')
  }, [recovery, replace, status])

  if (!recovery) return null

  const maskedContact = channel => channel === CHANNELS.sms ? recovery.maskedPhone : recovery.maskedEmail

  const getToken = async (force = false) => {
    if (!force && Date.now() - recovery.receivedAt < TOKEN_TTL) return recovery.token
    const response = await dispatch(fetchPreregister(recovery.login)).unwrap()
    if (!response?.recovery?.token) throw new Error('Recovery token unavailable')
    return response.recovery.token
  }

  const showRequestError = ({ message, code }) => {
    setError(message || t(code === 'invalid_token' ? 'recoverySessionExpired' : 'somethingWentWrong'))
  }

  const sendCode = async (channel, retry = true) => {
    if (sendingChannel || isLoading) return
    setSendingChannel(channel)
    setError('')

    try {
      const token = await getToken()
      await POST(accountRecoveryCode, { body: { token, channel } }, false)
      setSent(prev => ({ ...prev, [channel]: true }))
      setSendingChannel('')

    } catch (err) {
      const parsed = parseRequestError(err)
      setSendingChannel('')
      if (parsed.code === 'invalid_token' && retry) {
        try {
          await getToken(true)
          return sendCode(channel, false)
        } catch (_) { }
      }
      showRequestError(parsed)
    }
  }

  const changeHandler = e => {
    if (!e) return
    const { name, value } = e.target
    const newValue = name === 'smsCode' || name === 'emailCode' ? value.replace(/\s/g, '') : value
    setForm(prev => ({ ...prev, [name]: newValue }))
    if (warning[name]) setWarning(prev => ({ ...prev, [name]: false }))
    if (error) setError('')
  }

  const blurHandler = e => {
    if (!e) return
    const { name, value } = e.target
    if (name === 'password' && value) {
      setWarning(prev => ({ ...prev, password: passwordErrorMessage(value, t) }))
    } else if (name === 'passwordSec' && value) {
      setWarning(prev => ({ ...prev, passwordSec: value !== form.password ? t('passwordsDoNotMatch') : false }))
    }
  }

  const validate = () => {
    const sentChannels = channels.filter(c => sent[c])
    const filledCodes = sentChannels.filter(c => form[CODE_FIELDS[c]])
    const newWarning = {
      password: passwordErrorMessage(form.password, t),
      passwordSec: form.password !== form.passwordSec ? t('passwordsDoNotMatch') : false,
    }

    if (requiresBoth) {
      sentChannels.forEach(c => {
        if (!form[CODE_FIELDS[c]]) newWarning[CODE_FIELDS[c]] = t('enterCode')
      })
    } else if (filledCodes.length === 0) {
      sentChannels.forEach(c => { newWarning[CODE_FIELDS[c]] = t('enterCode') })
    }

    if (Object.values(newWarning).some(Boolean)) {
      setWarning(newWarning)
      return
    }

    if (requiresBoth && channels.some(c => !sent[c])) {
      setError(t('bothCodesRequired'))
      return
    }

    submit(filledCodes)
  }

  const submit = async (filledCodes) => {
    setStatus(STATUS.pending)
    setError('')

    try {
      const token = await getToken()
      const body = {
        token,
        password: form.password,
        password_confirmation: form.passwordSec,
      }
      if (filledCodes.includes(CHANNELS.sms)) body.sms_code = form.smsCode
      if (filledCodes.includes(CHANNELS.email)) body.email_code = form.emailCode

      const result = await dispatch(recoverAccount(body))
      if (recoverAccount.fulfilled.match(result) && result.payload?.access_token) {
        setStatus(STATUS.succeed)
        replace('/login')
        return
      }

      const parsed = result.payload || {}
      setStatus(STATUS.failed)

      if (parsed.code === 'invalid_token') {
        try { await getToken(true) } catch (_) { }
        setSent({})
        setForm(prev => ({ ...prev, smsCode: '', emailCode: '' }))
      } else if (parsed.code === 'invalid_code' || parsed.code === 'code_required') {
        const codeWarning = {}
        filledCodes.forEach(c => { codeWarning[CODE_FIELDS[c]] = parsed.message || t('wrongCode') })
        if (filledCodes.length > 0) {
          setWarning(prev => ({ ...prev, ...codeWarning }))
          return
        }
      }
      showRequestError(parsed)

    } catch (err) {
      setStatus(STATUS.failed)
      showRequestError(parseRequestError(err))
    }
  }

  const enterClicked = ref => {
    const index = refs.findIndex(r => Object.is(r, ref))
    if (index === -1) return
    if (refs[index + 1]) refs[index + 1].focus()
    else if (index + 1 === refs.length && !isLoading) validate()
  }

  const channelButton = channel => <button
    key={channel}
    type='button'
    className='button--social'
    disabled={!!sendingChannel || isLoading}
    onClick={() => sendCode(channel)}>
    <i className={`icon icon--${channel === CHANNELS.sms ? 'phone' : 'mail'}`} />
    <span>
      {sendingChannel === channel
        ? t('sending')
        : `${t(sent[channel] ? 'resendCodeTo' : channel === CHANNELS.sms ? 'sendSmsCodeTo' : 'sendCodeTo')} ${maskedContact(channel) || ''}`}
    </span>
  </button>

  return (
    <div className='reset__password recovery'>
      <div className='reset__title title'>
        <div>{t('recoverPassword')}</div>
        <div>{t(isFormStep ? 'recoveryFormDesc' : requiresBoth ? 'recoveryBothCodesDesc' : 'recoveryDesc')}</div>
      </div>

      <div className='reset__main main center'>
        <div className='reset__form form'>
          {recovery.login?.phone && recovery.login?.email && recovery.matchedBy === 'phone' && <div className='recovery__info'>
            {t('recoveryMatchedByPhone')}
          </div>}

          {error && <div className='recovery__error'>{error}</div>}

          <div className='recovery__channels'>
            {channels
              .filter(c => !isFormStep || sent[c] || requiresBoth)
              .map(c => sent[c]
                ? <div key={c} className='recovery__code'>
                  <Input
                    required
                    ref={r => refs.push(r)}
                    value={form[CODE_FIELDS[c]]}
                    name={CODE_FIELDS[c]}
                    type='tel'
                    inputMode='numeric'
                    autoComplete='one-time-code'
                    label={`${t(c === CHANNELS.sms ? 'smsCode' : 'emailCode')} (${maskedContact(c) || ''})`}
                    maxLength='12'
                    disabled={isLoading}
                    warning={warning[CODE_FIELDS[c]] || undefined}
                    placeholder={t('enterCode')}
                    enterClicked={enterClicked}
                    changeHandler={changeHandler} />
                  <BackBtn
                    text={sendingChannel === c ? t('sending') : t('resendCode')}
                    action={() => sendCode(c)} />
                </div>
                : channelButton(c))}
          </div>

          {isFormStep && <>
            <Input
              required
              ref={r => refs.push(r)}
              value={form.password}
              name='password'
              type='password'
              label={t('newPassword')}
              minLength='8'
              maxLength='100'
              disabled={isLoading}
              autoComplete='new-password'
              warning={warning.password || undefined}
              placeholder={t('enterANewPassword')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <Input
              required
              ref={r => refs.push(r)}
              value={form.passwordSec}
              name='passwordSec'
              type='password'
              label={t('repeatNewPassword')}
              minLength='8'
              maxLength='100'
              disabled={isLoading}
              autoComplete='new-password'
              warning={warning.passwordSec || undefined}
              placeholder={t('reEnterNewPassword')}
              blurHandler={blurHandler}
              enterClicked={enterClicked}
              changeHandler={changeHandler} />

            <ArrowBtn
              enlarged
              isLoading={isLoading}
              text={t('setNewPassword')}
              action={() => !isLoading && validate()} />
          </>}

          <p className='recovery__noAccess'>{t('noAccessToTheseContacts')}</p>
          <button
            type='button'
            className='button--social'
            onClick={() => {
              if (config?.phoneSupport) window.location.href = `tel:${config.phoneSupport}`
              else push('/help')
            }}>
            <i className='icon icon--call' />
            <span>{t('contactUs')}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default AccountRecovery
